// webmMuxer.js — minimal in-memory WebM writer for WebCodecs output.
//
// Why not MediaRecorder: it records in real time, and on a modest GPU the
// encoder falls behind a 1080p canvas — whatever is still queued when stop()
// is called is silently dropped (the export lost its last ~3s). WebCodecs
// encodes at its own pace with explicit timestamps, and writing the file
// ourselves also gives it a real Duration + Cues, so the download is seekable
// everywhere (MediaRecorder WebM has neither).
//
// Layout: EBML header, Segment{ Info(Duration), Tracks, Cues, Cluster... }.
// Cues sit BEFORE the clusters and use fixed-width positions, so their size is
// known before the cluster offsets are. One video track (VP8/VP9) and an
// optional Opus audio track. Everything is held in memory — fine for the
// minute-scale clips this exports.

const ID = {
  EBML: 0x1a45dfa3,
  EBMLVersion: 0x4286,
  EBMLReadVersion: 0x42f7,
  EBMLMaxIDLength: 0x42f2,
  EBMLMaxSizeLength: 0x42f3,
  DocType: 0x4282,
  DocTypeVersion: 0x4287,
  DocTypeReadVersion: 0x4285,
  Segment: 0x18538067,
  Info: 0x1549a966,
  TimecodeScale: 0x2ad7b1,
  MuxingApp: 0x4d80,
  WritingApp: 0x5741,
  Duration: 0x4489,
  Tracks: 0x1654ae6b,
  TrackEntry: 0xae,
  TrackNumber: 0xd7,
  TrackUID: 0x73c5,
  TrackType: 0x83,
  CodecID: 0x86,
  CodecPrivate: 0x63a2,
  SeekPreRoll: 0x56bb,
  Video: 0xe0,
  PixelWidth: 0xb0,
  PixelHeight: 0xba,
  Audio: 0xe1,
  SamplingFrequency: 0xb5,
  Channels: 0x9f,
  Cues: 0x1c53bb6b,
  CuePoint: 0xbb,
  CueTime: 0xb3,
  CueTrackPositions: 0xb7,
  CueTrack: 0xf7,
  CueClusterPosition: 0xf1,
  Cluster: 0x1f43b675,
  Timecode: 0xe7,
  SimpleBlock: 0xa3,
};

function concat(parts) {
  let len = 0;
  for (const p of parts) len += p.length;
  const out = new Uint8Array(len);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

function idBytes(id) {
  const out = [];
  let v = id;
  while (v > 0) {
    out.unshift(v & 0xff);
    v = Math.floor(v / 256);
  }
  return Uint8Array.from(out);
}

// EBML variable-length size. Widths 1..8; 2^56-2 max is far beyond our needs.
function vint(n) {
  for (let w = 1; w <= 8; w++) {
    if (n < Math.pow(2, 7 * w) - 1) {
      const out = new Uint8Array(w);
      let v = n;
      for (let i = w - 1; i >= 0; i--) {
        out[i] = v & 0xff;
        v = Math.floor(v / 256);
      }
      out[0] |= 1 << (8 - w);
      return out;
    }
  }
  throw new Error("ebml-size-too-large");
}

function uintBytes(n, width) {
  const w = width || Math.max(1, Math.ceil(Math.log2(n + 1) / 8));
  const out = new Uint8Array(w);
  let v = n;
  for (let i = w - 1; i >= 0; i--) {
    out[i] = v & 0xff;
    v = Math.floor(v / 256);
  }
  return out;
}

const el = (id, payload) => concat([idBytes(id), vint(payload.length), payload]);
const master = (id, children) => el(id, concat(children));
const uint = (id, n, width) => el(id, uintBytes(n, width));
const str = (id, s) => el(id, new TextEncoder().encode(s));
const float64 = (id, f) => {
  const b = new Uint8Array(8);
  new DataView(b.buffer).setFloat64(0, f);
  return el(id, b);
};

/** "OpusHead" identification header (RFC 7845 §5.1), mapping family 0. */
export function opusHead(channels, sampleRate, preSkip = 0) {
  const b = new Uint8Array(19);
  b.set(new TextEncoder().encode("OpusHead"), 0);
  const dv = new DataView(b.buffer);
  b[8] = 1; // version
  b[9] = channels;
  dv.setUint16(10, preSkip, true);
  dv.setUint32(12, sampleRate, true);
  dv.setInt16(16, 0, true); // output gain
  b[18] = 0; // channel mapping family
  return b;
}

/**
 * @param {object} cfg
 * @param {{codec:'V_VP9'|'V_VP8', width:number, height:number}} cfg.video
 * @param {{sampleRate:number, channels:number}} [cfg.audio]  Opus
 */
export function createWebmMuxer(cfg) {
  const blocks = []; // { track, tsMs, key, data }

  function add(track, chunk) {
    const data = new Uint8Array(chunk.byteLength);
    chunk.copyTo(data);
    blocks.push({
      track,
      tsMs: Math.round(chunk.timestamp / 1000),
      key: chunk.type === "key",
      data,
    });
  }

  return {
    addVideoChunk: (chunk) => add(1, chunk),
    addAudioChunk: (chunk) => add(2, chunk),

    /** @returns {Blob} video/webm */
    finalize(durationMs) {
      // Stable order: by time, video before audio on ties.
      blocks.sort((a, b) => a.tsMs - b.tsMs || a.track - b.track);

      // Clusters start on video keyframes (and before the int16 relative
      // timecode would overflow).
      const clusters = [];
      let cur = null;
      for (const b of blocks) {
        const newCluster = !cur || (b.track === 1 && b.key) || b.tsMs - cur.tsMs > 30000;
        if (newCluster) {
          cur = { tsMs: b.tsMs, blocks: [], hasKey: b.track === 1 && b.key };
          clusters.push(cur);
        }
        cur.blocks.push(b);
      }
      const clusterBytes = clusters.map((c) =>
        master(ID.Cluster, [
          uint(ID.Timecode, c.tsMs),
          ...c.blocks.map((b) => {
            const head = new Uint8Array(4);
            head[0] = 0x80 | b.track; // track number as 1-byte vint
            new DataView(head.buffer).setInt16(1, b.tsMs - c.tsMs);
            head[3] = b.key || b.track === 2 ? 0x80 : 0x00;
            return el(ID.SimpleBlock, concat([head, b.data]));
          }),
        ])
      );

      const info = master(ID.Info, [
        uint(ID.TimecodeScale, 1000000),
        str(ID.MuxingApp, "aidea-studio"),
        str(ID.WritingApp, "aidea-studio"),
        float64(ID.Duration, durationMs),
      ]);
      const tracks = [
        master(ID.TrackEntry, [
          uint(ID.TrackNumber, 1),
          uint(ID.TrackUID, 1),
          uint(ID.TrackType, 1),
          str(ID.CodecID, cfg.video.codec),
          master(ID.Video, [
            uint(ID.PixelWidth, cfg.video.width),
            uint(ID.PixelHeight, cfg.video.height),
          ]),
        ]),
      ];
      if (cfg.audio) {
        tracks.push(
          master(ID.TrackEntry, [
            uint(ID.TrackNumber, 2),
            uint(ID.TrackUID, 2),
            uint(ID.TrackType, 2),
            str(ID.CodecID, "A_OPUS"),
            el(ID.CodecPrivate, opusHead(cfg.audio.channels, cfg.audio.sampleRate)),
            uint(ID.SeekPreRoll, 80000000),
            master(ID.Audio, [
              float64(ID.SamplingFrequency, cfg.audio.sampleRate),
              uint(ID.Channels, cfg.audio.channels),
            ]),
          ])
        );
      }
      const tracksEl = master(ID.Tracks, tracks);

      // Cues for keyframe clusters. Positions are relative to the Segment
      // payload start; fixed 8-byte width keeps the Cues size independent of
      // the offsets it encodes.
      const keyIdx = clusters.map((c, i) => (c.hasKey ? i : -1)).filter((i) => i >= 0);
      const buildCues = (positions) =>
        master(
          ID.Cues,
          keyIdx.map((i) =>
            master(ID.CuePoint, [
              uint(ID.CueTime, clusters[i].tsMs, 8),
              master(ID.CueTrackPositions, [
                uint(ID.CueTrack, 1),
                uint(ID.CueClusterPosition, positions[i], 8),
              ]),
            ])
          )
        );
      const cuesSize = buildCues(clusters.map(() => 0)).length;
      const positions = [];
      let off = info.length + tracksEl.length + cuesSize;
      for (const cb of clusterBytes) {
        positions.push(off);
        off += cb.length;
      }
      const cues = buildCues(positions);

      const header = master(ID.EBML, [
        uint(ID.EBMLVersion, 1),
        uint(ID.EBMLReadVersion, 1),
        uint(ID.EBMLMaxIDLength, 4),
        uint(ID.EBMLMaxSizeLength, 8),
        str(ID.DocType, "webm"),
        uint(ID.DocTypeVersion, 4),
        uint(ID.DocTypeReadVersion, 2),
      ]);
      const segPayload = [info, tracksEl, cues, ...clusterBytes];
      let segLen = 0;
      for (const p of segPayload) segLen += p.length;
      return new Blob([header, idBytes(ID.Segment), vint(segLen), ...segPayload], {
        type: "video/webm",
      });
    },
  };
}
